import { api } from "../api";
import { esc } from "../utils/html";

interface Endpoint {
  method: string; path: string; description: string; auth: string;
  body?: string; response?: string;
}
interface EndpointGroup {
  group: string; description: string; auth: string; endpoints: Endpoint[];
}

let groups: EndpointGroup[] = [];
let searchTerm = "";

function methodColor(method: string): string {
  switch (method) {
    case "GET": return "#22c55e";
    case "POST": return "#3b82f6";
    case "PUT": return "#f59e0b";
    case "PATCH": return "#a855f7";
    case "DELETE": return "#ef4444";
    default: return "#6b7280";
  }
}

function authBadge(auth: string): string {
  if (auth === "Keine") return `<span class="api-auth-badge api-auth-none">Öffentlich</span>`;
  if (auth.includes("plattform-admin")) return `<span class="api-auth-badge api-auth-admin">Plattform-Admin</span>`;
  return `<span class="api-auth-badge api-auth-tenant">JWT + Mandant</span>`;
}

function renderGroups(el: HTMLElement) {
  const container = el.querySelector("#api-groups")!;
  const filtered = groups.map(g => {
    if (!searchTerm) return g;
    const term = searchTerm.toLowerCase();
    const matchGroup = g.group.toLowerCase().includes(term) || g.description.toLowerCase().includes(term);
    const matchedEndpoints = g.endpoints.filter(e =>
      e.path.toLowerCase().includes(term) ||
      e.method.toLowerCase().includes(term) ||
      e.description.toLowerCase().includes(term)
    );
    if (matchGroup) return g;
    if (matchedEndpoints.length > 0) return { ...g, endpoints: matchedEndpoints };
    return null;
  }).filter(Boolean) as EndpointGroup[];

  if (filtered.length === 0) {
    container.innerHTML = `<div class="empty-state"><p>Keine Endpunkte gefunden für „${esc(searchTerm)}".</p></div>`;
    return;
  }

  // Endpunkt-Zähler
  const total = filtered.reduce((n, g) => n + g.endpoints.length, 0);
  el.querySelector("#api-count")!.textContent = `${total} Endpunkte in ${filtered.length} Gruppen`;

  container.innerHTML = filtered.map(g => `
    <div class="api-group">
      <div class="api-group-header">
        <div>
          <h2 class="api-group-title">${esc(g.group)}</h2>
          <div class="api-group-desc">${esc(g.description)}</div>
        </div>
        ${authBadge(g.auth)}
      </div>
      <div class="api-endpoints">
        ${g.endpoints.map(e => `
          <details class="api-endpoint">
            <summary class="api-endpoint-summary">
              <span class="api-method" style="background:${methodColor(e.method)}">${e.method}</span>
              <code class="api-path">${esc(e.path)}</code>
              <span class="api-desc">${esc(e.description)}</span>
            </summary>
            <div class="api-endpoint-detail">
              <table class="api-detail-table">
                <tr><td class="api-detail-label">Authentifizierung</td><td>${authBadge(e.auth)}</td></tr>
                ${e.body ? `<tr><td class="api-detail-label">Request-Body</td><td><code class="api-code-block">${esc(e.body)}</code></td></tr>` : ""}
                ${e.response ? `<tr><td class="api-detail-label">Response</td><td><code class="api-code-block">${esc(e.response)}</code></td></tr>` : ""}
              </table>
            </div>
          </details>
        `).join("")}
      </div>
    </div>
  `).join("");
}

export async function render(el: HTMLElement) {
  el.innerHTML = `
    <div class="page-header">
      <div>
        <h1>API-Dokumentation</h1>
        <div style="font-size:.875rem;color:var(--gray-500);margin-top:.25rem" id="api-count">Wird geladen…</div>
      </div>
    </div>
    <div class="api-search-bar">
      <input type="text" id="api-search" placeholder="Endpunkte durchsuchen… (z.B. GET, klassen, admin)" />
    </div>
    <div class="api-legend">
      <span><span class="api-method" style="background:#22c55e">GET</span> Lesen</span>
      <span><span class="api-method" style="background:#3b82f6">POST</span> Erstellen</span>
      <span><span class="api-method" style="background:#f59e0b">PUT</span> Bearbeiten</span>
      <span><span class="api-method" style="background:#a855f7">PATCH</span> Teilupdate</span>
      <span><span class="api-method" style="background:#ef4444">DELETE</span> Löschen</span>
    </div>
    <div id="api-groups"></div>
  `;

  // Suche
  const searchInput = el.querySelector("#api-search") as HTMLInputElement;
  let debounce: any;
  searchInput.addEventListener("input", () => {
    clearTimeout(debounce);
    debounce = setTimeout(() => { searchTerm = searchInput.value.trim(); renderGroups(el); }, 200);
  });

  // Daten laden
  try {
    groups = await api.admin.apiDocs();
    renderGroups(el);
  } catch (err: any) {
    const errDiv = document.createElement("div");
    errDiv.className = "alert alert-error";
    errDiv.textContent = `Fehler beim Laden: ${err.message}`;
    el.querySelector("#api-groups")!.replaceChildren(errDiv);
  }
}
